import {
  type FunctionComponent,
  type Ref,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

import {
  Button,
  Checkbox,
  CustomDropdown,
  Input,
} from '@dr.pogodin/react-utils';

import { captureException } from '@sentry/react';

import InputWithValidation from 'components/InputWithValidation';
import Throbber from 'components/Throbber';
import RequiredField from 'components/admin/RequiredField';

import { updateUser } from 'services/user';

import {
  PAYMENT_METHOD_OPTIONS,
  type PaymentMethodT,
  getId,
  isValidName,
  isValidPaymentMethod,
  isValidPhoneOrEmpty,
  useGlobalState,
} from 'utils';

import type { TabRefT } from '../common';

import checkboxTheme from './checkbox.scss';

import S from './style.scss';

type HeapT = {
  onValidChanged?: (valid: boolean) => void;
};

type PropsT = {
  goToNextTab: () => void;
  onValidChanged: (valid: boolean) => void;
  ref: Ref<TabRefT>;
};

const Tab01: FunctionComponent<PropsT> = ({
  goToNextTab,
  onValidChanged,
  ref,
}) => {
  const { current: heap } = useRef<HeapT>({});
  // eslint-disable-next-line react-hooks/refs
  heap.onValidChanged = onValidChanged;

  const [user, setUser] = useGlobalState('user');
  if (!user) throw Error('Unauthorized');

  const [firstName, setFirstName] = useState(user.firstName);
  const [firstNameError, setFirstNameError] = useState('');
  const firstNameValid = isValidName(firstName);

  const [lastName, setLastName] = useState(user.lastName);
  const [lastNameError, setLastNameError] = useState('');
  const lastNameValid = isValidName(lastName);

  const [phone, setPhone] = useState(user.phone ?? '');
  const phoneValid = isValidPhoneOrEmpty(phone);

  const valid: boolean = firstNameValid && lastNameValid && phoneValid;

  useEffect(() => {
    if (!heap.onValidChanged) throw Error('Missing callback');
    heap.onValidChanged(valid);
    // eslint-disable-next-line react-hooks/refs
  }, [heap, valid]);

  const [
    phoneAllowsWhatsApp,
    setPhoneAllowsWhatsApp,
  ] = useState(user.phoneAllowsWhatsApp ?? false);

  const [city, setCity] = useState(user.city ?? '');
  const [country, setCountry] = useState(user.state ?? '');

  const [
    paymentMethod,
    setPaymentMethod,
  ] = useState(user.paymentMethod ?? undefined);

  const [paymentError, setPaymentError] = useState('');

  const [busy, setBusy] = useState<boolean>(false);

  const save = useCallback(async () => {
    setBusy(true);
    try {
      // TODO: In theory, we can check if the local data are dirty, and only
      // fire the save request when necessary... in practice, let's just always
      // save for now, at least until the tab content is stable.
      const newUser = await updateUser(getId(user), {
        city,
        firstName,
        lastName,
        paymentMethod,
        phone,
        phoneAllowsWhatsApp,
        state: country,
      });
      setUser(newUser);

      /*
      await practitioner.reload();
      */
    } catch (error) {
      captureException(error);
      // eslint-disable-next-line no-alert
      alert('Failed to save data');
    }
    setBusy(false);
  }, [
    city,
    country,
    firstName,
    lastName,
    paymentMethod,
    phone,
    phoneAllowsWhatsApp,
    setUser,
    user,
  ]);

  useImperativeHandle(ref, () => ({
    save,
    valid,
  }), [save, valid]);

  return (
    <div className={S.container}>
      <p styleName="twoColumns">
        Share your contact details and upload an introduction video so we can
        meet the real you.
      </p>
      <div styleName="withUnderLabel">
        <Input
          error={firstNameError}
          label="First Name"
          onBlur={() => {
            if (!firstNameValid) setFirstNameError('This field is required');
          }}
          onChange={(e) => {
            const { value } = e.target;
            setFirstName(value);
            if (firstNameError && isValidName(value)) {
              setFirstNameError('');
            }
          }}
          value={firstName}
        />
        {firstNameError ? null : <RequiredField />}
      </div>
      <div styleName="withUnderLabel">
        <Input
          error={lastNameError}
          label="Last Name"
          onBlur={() => {
            if (!lastNameValid) setLastNameError('This field is required');
          }}
          onChange={(e) => {
            const { value } = e.target;
            setLastName(value);
            if (lastNameError && isValidName(value)) {
              setLastNameError('');
            }
          }}
          value={lastName}
        />
        {lastNameError ? null : <RequiredField />}
      </div>
      {/* Email is not editable for now, as it is should be a very special
      flow to edit it safely. */}
      <Input disabled label="Email" value={user.email ?? ''} />
      <div />
      <div styleName="phoneBlock">
        <InputWithValidation
          label="Phone"
          setValue={setPhone}
          validator={isValidPhoneOrEmpty}
          value={phone}
        />
        <Checkbox
          checked={phoneAllowsWhatsApp}
          label="Same number for WhatsApp"
          onChange={() => {
            setPhoneAllowsWhatsApp(!phoneAllowsWhatsApp);
          }}
          theme={checkboxTheme}
        />
      </div>
      <CustomDropdown
        label="Payment Method"
        onChange={(value) => {
          const pm = value as PaymentMethodT;
          setPaymentMethod(pm);
          if (paymentError && isValidPaymentMethod(pm)) setPaymentError('');
        }}
        options={PAYMENT_METHOD_OPTIONS}
        value={paymentMethod}
      />
      <Input
        label="City"
        onChange={(e) => {
          setCity(e.target.value);
        }}
        value={city}
      />
      <Input
        label="Country"
        onChange={(e) => {
          setCountry(e.target.value);
        }}
        value={country}
      />
      <div styleName="hr twoColumns" />
      <div styleName="buttons">
        <Button
          disabled={!valid}
          onClick={() => {
            void (async () => {
              await save();
              goToNextTab();
            })();
          }}
        >
          Next
        </Button>
      </div>
      {busy ? <Throbber withOverlay /> : null}
    </div>
  );
};

export default Tab01;
